import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import { useSelector } from 'react-redux';
import { Socket, io } from 'socket.io-client';

import { SOCKET_NAMESPACE, SOCKET_SERVER_URL } from '../config/Constants';

type SocketContextType = {
  socket: Socket | null;
  isConnected: boolean;
  subscribe: (event: string, callback: (data: any) => void) => () => void;
  joinRoom: (room: string) => void;
  leaveRoom: (room: string) => void;
  reconnect: () => void;
};

const SocketContext = createContext<SocketContextType>({
  socket: null,
  isConnected: false,
  subscribe: () => () => {
    /* default no-op subscribe */
  },
  joinRoom: () => {
    /* default no-op joinRoom */
  },
  leaveRoom: () => {
    /* default no-op leaveRoom */
  },
  reconnect: () => {
    /* default no-op reconnect */
  },
});

export const useSocket = () => useContext(SocketContext);

export const useSocketEvent = (
  event: string,
  callback: (data: any) => void
) => {
  const { subscribe } = useSocket();

  useEffect(() => {
    return subscribe(event, callback);
  }, [event, callback, subscribe]);
};

export const useSocketRoom = (room: string | null | undefined) => {
  const { joinRoom, leaveRoom, isConnected } = useSocket();

  useEffect(() => {
    if (room && isConnected) {
      joinRoom(room);
      return () => leaveRoom(room);
    }
    return undefined;
  }, [room, isConnected, joinRoom, leaveRoom]);
};

type SocketProviderProps = {
  children: React.ReactNode;
};

export const SocketProvider: React.FC<SocketProviderProps> = ({ children }) => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const token = useSelector((state: any) => state.auth.token);

  const connect = useCallback((authToken: string) => {
    const socketInstance = io(`${SOCKET_SERVER_URL}/${SOCKET_NAMESPACE}`, {
      transports: ['websocket', 'polling'],
      reconnectionAttempts: 5,
      reconnectionDelay: 5000,
      auth: {
        token: `Bearer ${authToken}`,
      },
    });

    socketInstance.on('connect', () => {
      console.log('Socket.IO conectado con ID:', socketInstance.id);
      setIsConnected(true);
    });

    socketInstance.on('disconnect', () => {
      console.log('Socket.IO desconectado');
      setIsConnected(false);
    });

    socketInstance.on('connect_error', (error) => {
      console.error('Error de conexión Socket.IO:', error);
      setIsConnected(false);
    });

    setSocket(socketInstance);
    return socketInstance;
  }, []);

  useEffect(() => {
    if (!token) {
      if (socket) {
        console.log('Token perdido. Cerrando socket...');
        socket.disconnect();
        setSocket(null);
        setIsConnected(false);
      }
      return;
    }

    if (socket) {
      console.log('Nuevo token detectado. Cerrando socket anterior...');
      socket.disconnect();
      setSocket(null);
      setIsConnected(false);
    }

    console.log('Conectando socket con nuevo token...');
    const socketInstance = connect(token);

    return () => {
      socketInstance.disconnect();
      setSocket(null);
      setIsConnected(false);
    };
  }, [token]);

  const subscribe = useCallback(
    (event: string, callback: (data: any) => void) => {
      if (!socket) {
        console.warn(
          `No se puede suscribir a '${event}' - Socket no inicializado`
        );
        return () => {
          /* no-op unsubscribe */
        };
      }

      const wrappedCallback = (...args: any[]) => {
        const data = args.length === 1 ? args[0] : args;
        callback(data);
      };

      console.log(`Suscrito a evento: ${event}`);
      socket.on(event, wrappedCallback);

      return () => {
        console.log(`Cancelando suscripción a: ${event}`);
        socket.off(event, wrappedCallback);
      };
    },
    [socket]
  );

  const joinRoom = useCallback(
    (room: string) => {
      if (!socket || !socket.connected) {
        console.warn(`No se puede unir a sala '${room}' - Socket no conectado`);
        return;
      }
      socket.emit('join', room);
      console.log(`Unido a sala: ${room}`);
    },
    [socket]
  );

  const leaveRoom = useCallback(
    (room: string) => {
      if (!socket || !socket.connected) {
        return;
      }
      socket.emit('leave', room);
      console.log(`Saliendo de sala: ${room}`);
    },
    [socket]
  );

  const reconnect = useCallback(() => {
    if (socket) {
      console.log('Reconectando socket manualmente...');
      socket.disconnect();
    }
    if (token) {
      connect(token);
    }
  }, [socket, connect, token]);

  const value = {
    socket,
    isConnected,
    subscribe,
    joinRoom,
    leaveRoom,
    reconnect,
  };

  return (
    <SocketContext.Provider value={value}>{children}</SocketContext.Provider>
  );
};
